import React from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { 
  Car, Shield, FileText, Calculator, CircleDollarSign, Truck,
  Phone, MessageCircle, CheckCircle, ArrowLeft, Clock, Award
} from "lucide-react";

const serviceData = {
  "unfallgutachten": {
    title: "Unfallgutachten",
    metaTitle: "Unfallgutachten Hamburg & Schleswig-Holstein",
    metaDescription: "Professionelle Unfallgutachten vom unabhängigen Kfz-Sachverständigen. Innerhalb 60 Min. vor Ort, Gutachten in 24h. Kostenlos bei unverschuldetem Unfall. ☎ 0174 893 6514",
    icon: Car,
    heroImage: "https://static.prod-images.emergentagent.com/jobs/4aee379d-d7a9-43ea-b5e1-4f4900c746b3/images/241b08b6be6c51d60ef3537157386b10c00116192a882dcf3ea6bfb08740a08c.png",
    intro: "Ein Unfallgutachten ist nach einem Verkehrsunfall unverzichtbar, um Ihre Ansprüche gegenüber der Versicherung vollständig durchzusetzen. Als unabhängiger Kfz-Sachverständiger erstellen wir detaillierte und rechtssichere Gutachten in Hamburg und Schleswig-Holstein.",
    sections: [
      {
        title: "Was beinhaltet ein Unfallgutachten?",
        content: "Ein professionelles Unfallgutachten dokumentiert den gesamten Schadensumfang Ihres Fahrzeugs nach einem Unfall. Es umfasst eine detaillierte Fotodokumentation, die Berechnung der Reparaturkosten, die Ermittlung des Wiederbeschaffungswerts und Restwerts sowie die Feststellung einer möglichen merkantilen Wertminderung.",
        list: [
          "Vollständige Fotodokumentation aller Schäden",
          "Detaillierte Schadenskalkulation",
          "Wiederbeschaffungswert-Ermittlung",
          "Restwert-Berechnung",
          "Merkantile Wertminderung",
          "Reparaturdauer-Schätzung"
        ]
      },
      {
        title: "Wann brauche ich ein Unfallgutachten?",
        content: "Ein Unfallgutachten ist immer dann sinnvoll, wenn der Schaden die Bagatellgrenze von ca. 750 Euro übersteigt. Bei einem unverschuldeten Unfall haben Sie das Recht, einen eigenen unabhängigen Gutachter zu beauftragen – die Kosten trägt die gegnerische Versicherung.",
        list: [
          "Unverschuldeter Unfall",
          "Schaden über 750 Euro",
          "Wirtschaftlicher Totalschaden möglich",
          "Wertminderung geltend machen",
          "Fiktive Abrechnung gewünscht"
        ]
      },
      {
        title: "Ihre Vorteile bei uns",
        content: "Als unabhängiger Sachverständiger arbeiten wir ausschließlich in Ihrem Interesse – nicht im Interesse der Versicherung. Wir sind schnell vor Ort, erstellen Ihr Gutachten innerhalb von 24 Stunden und übernehmen auf Wunsch die komplette Kommunikation mit der Versicherung.",
        list: [
          "Innerhalb 60 Minuten vor Ort",
          "Gutachten in 24 Stunden",
          "Kostenlos bei unverschuldetem Unfall",
          "Direkte Versicherungsabwicklung",
          "Erreichbar Mo-So 9-23 Uhr"
        ]
      }
    ],
    faq: [
      { q: "Wer zahlt das Unfallgutachten?", a: "Bei einem unverschuldeten Unfall übernimmt die Haftpflichtversicherung des Unfallverursachers die Kosten für das Gutachten." },
      { q: "Wie schnell erhalte ich mein Gutachten?", a: "In der Regel erhalten Sie Ihr vollständiges Gutachten innerhalb von 24 Stunden nach der Fahrzeugbegutachtung." },
      { q: "Kann ich den Gutachter frei wählen?", a: "Ja, bei einem unverschuldeten Unfall haben Sie das Recht, einen eigenen unabhängigen Gutachter Ihrer Wahl zu beauftragen." }
    ]
  },
  "haftpflichtgutachten": {
    title: "Haftpflichtgutachten",
    metaTitle: "Haftpflichtgutachten bei unverschuldetem Unfall",
    metaDescription: "Haftpflichtgutachten vom unabhängigen Sachverständigen. Kostenübernahme durch gegnerische Versicherung. Wir setzen Ihre Ansprüche durch. Hamburg & Schleswig-Holstein.",
    icon: Shield,
    heroImage: null,
    intro: "Ein Haftpflichtgutachten benötigen Sie, wenn Sie unverschuldet in einen Unfall verwickelt wurden. Die Kosten werden von der gegnerischen Versicherung getragen. Wir dokumentieren Ihren Schaden objektiv und setzen Ihre Ansprüche durch.",
    sections: [
      {
        title: "Was ist ein Haftpflichtgutachten?",
        content: "Ein Haftpflichtgutachten ist ein Schadensgutachten, das erstellt wird, wenn Sie nicht der Verursacher des Unfalls sind. Es dokumentiert den Schaden an Ihrem Fahrzeug und bildet die Grundlage für Ihre Ansprüche gegenüber der Haftpflichtversicherung des Unfallverursachers.",
        list: ["Objektive Schadensdokumentation", "Kostenübernahme durch Unfallgegner", "Rechtssichere Grundlage für Ansprüche", "Unabhängige Bewertung"]
      },
      {
        title: "Ihre Rechte nach § 249 BGB",
        content: "Nach § 249 BGB haben Sie das Recht auf vollständige Schadensregulierung. Das bedeutet: Die gegnerische Versicherung muss den Schaden so ersetzen, als wäre er nicht entstanden. Ein unabhängiges Gutachten stellt sicher, dass alle Schadensposten erfasst werden.",
        list: ["Reparaturkosten in voller Höhe", "Wertminderung", "Nutzungsausfall oder Mietwagen", "Gutachterkosten", "Anwaltskosten"]
      }
    ],
    faq: [
      { q: "Muss ich das Gutachten vorstrecken?", a: "Nein, wir rechnen direkt mit der gegnerischen Versicherung ab. Sie müssen nichts vorstrecken." },
      { q: "Kann die Versicherung einen eigenen Gutachter schicken?", a: "Sie haben das Recht auf einen eigenen Gutachter. Nehmen Sie nicht den Gutachter der Gegenseite an." }
    ]
  },
  "kasko-schaden": {
    title: "Kasko-Schaden",
    metaTitle: "Kasko Gutachten - Teilkasko & Vollkasko",
    metaDescription: "Kfz-Gutachten für Kasko-Schäden: Teilkasko, Vollkasko, Hagelschäden, Wildunfälle. Unabhängige Zweitmeinung wenn die Versicherung zu wenig zahlt.",
    icon: FileText,
    heroImage: null,
    intro: "Bei einem Kasko-Schaden liegt das Weisungsrecht zur Beauftragung eines Gutachters bei Ihrer Versicherung. Dennoch kann ein unabhängiges Gutachten sinnvoll sein, um sicherzustellen, dass der Schaden vollständig erfasst wird.",
    sections: [
      {
        title: "Teilkasko vs. Vollkasko",
        content: "Die Teilkaskoversicherung deckt Schäden durch Diebstahl, Brand, Naturereignisse und Glasbruch. Die Vollkaskoversicherung schließt zusätzlich selbstverschuldete Unfälle und Vandalismus ein.",
        list: ["Teilkasko: Diebstahl, Hagel, Sturm, Brand, Wildunfälle", "Vollkasko: Selbstverschuldete Unfälle, Vandalismus", "Glasschäden", "Marderbiss"]
      },
      {
        title: "Wann lohnt sich ein eigenes Gutachten?",
        content: "Wenn Sie vermuten, dass die Versicherung den Schaden zu niedrig ansetzt, oder wenn ein Totalschaden droht, kann ein unabhängiges Zweitmeinung-Gutachten sinnvoll sein.",
        list: ["Zweitmeinung bei niedrigem Erstgutachten", "Bei drohendem Totalschaden", "Zur eigenen Absicherung"]
      }
    ],
    faq: [
      { q: "Wer bestimmt den Gutachter bei Kaskoschäden?", a: "Bei Kaskoschäden hat Ihre Versicherung das Weisungsrecht und bestimmt in der Regel den Gutachter." }
    ]
  },
  "kostenvoranschlag": {
    title: "Kostenvoranschlag",
    metaTitle: "Kfz Kostenvoranschlag - Reparaturkosten ermitteln",
    metaDescription: "Kfz-Kostenvoranschlag für kleinere Schäden. Schnelle und kostengünstige Alternative zum Gutachten. Transparente Kalkulation der Reparaturkosten.",
    icon: Calculator,
    heroImage: null,
    intro: "Ein Kostenvoranschlag gibt Ihnen einen Überblick über die zu erwartenden Reparaturkosten. Er ist eine kostengünstige Alternative zum vollständigen Gutachten bei kleineren Schäden.",
    sections: [
      {
        title: "Wann reicht ein Kostenvoranschlag?",
        content: "Bei Bagatellschäden unter ca. 750 Euro ist ein Kostenvoranschlag oft ausreichend. Er dokumentiert den Schaden und die voraussichtlichen Reparaturkosten ohne den Umfang eines vollständigen Gutachtens.",
        list: ["Kleinere Schäden unter 750 Euro", "Keine Wertminderung zu erwarten", "Schnelle Schadensregulierung gewünscht", "Kostengünstige Option"]
      },
      {
        title: "Unterschied zum Gutachten",
        content: "Im Gegensatz zum vollständigen Gutachten enthält ein Kostenvoranschlag keine Restwertermittlung, keine Wertminderungsberechnung und keine Wiederbeschaffungswert-Analyse.",
        list: ["Keine Restwertermittlung", "Keine Wertminderung", "Keine Wiederbeschaffungswert-Analyse", "Geringerer Umfang und Kosten"]
      }
    ],
    faq: [
      { q: "Was kostet ein Kostenvoranschlag?", a: "Ein Kostenvoranschlag ist deutlich günstiger als ein vollständiges Gutachten. Die genauen Kosten hängen vom Aufwand ab." }
    ]
  },
  "wertgutachten": {
    title: "Wertgutachten",
    metaTitle: "Kfz Wertgutachten & Fahrzeugbewertung",
    metaDescription: "Professionelle Fahrzeugbewertung und Wertgutachten. Für Kauf, Verkauf, Versicherung, Erbschaft oder Scheidung. Präzise Marktwertermittlung.",
    icon: CircleDollarSign,
    heroImage: null,
    intro: "Ein Wertgutachten ermittelt den aktuellen Marktwert Ihres Fahrzeugs. Es ist unerlässlich beim Kauf oder Verkauf, bei Versicherungsfragen oder bei rechtlichen Auseinandersetzungen.",
    sections: [
      {
        title: "Wann brauche ich ein Wertgutachten?",
        content: "Ein Wertgutachten ist in vielen Situationen sinnvoll: beim Fahrzeugkauf oder -verkauf, bei der Versicherungseinstufung, bei Erbschaften oder Scheidungen, oder zur Dokumentation des Fahrzeugzustands.",
        list: ["Fahrzeugkauf oder -verkauf", "Versicherungseinstufung", "Erbschaft oder Scheidung", "Leasingrückgabe", "Dienstwagen-Übernahme"]
      },
      {
        title: "Was wird bewertet?",
        content: "Bei einem Wertgutachten werden alle wertrelevanten Faktoren Ihres Fahrzeugs berücksichtigt: Zustand, Laufleistung, Ausstattung, Wartungszustand und eventuelle Vorschäden.",
        list: ["Technischer Zustand", "Optischer Zustand", "Laufleistung", "Sonderausstattung", "Wartungshistorie", "Vorschäden"]
      }
    ],
    faq: [
      { q: "Wie lange dauert die Erstellung?", a: "Ein Wertgutachten erstellen wir in der Regel innerhalb von 1-2 Werktagen nach der Fahrzeugbegutachtung." }
    ]
  },
  "oldtimer-gutachten": {
    title: "Oldtimer-Gutachten",
    metaTitle: "Oldtimer Gutachten & Klassiker Bewertung",
    metaDescription: "Professionelle Oldtimer-Bewertung und Gutachten für H-Kennzeichen. Zustandsbewertung, Marktwertermittlung, Originalitätsprüfung. Hamburg & Schleswig-Holstein.",
    icon: Truck,
    heroImage: "https://static.prod-images.emergentagent.com/jobs/4aee379d-d7a9-43ea-b5e1-4f4900c746b3/images/8c89ea046e5549062db9feaae22f3557075674a119a664b60ada39c1479e4929.png",
    intro: "Oldtimer sind mehr als nur Fahrzeuge – sie sind Leidenschaft und Wertanlage. Ein professionelles Oldtimer-Gutachten dokumentiert den Zustand und Wert Ihres Klassikers fachgerecht.",
    sections: [
      {
        title: "Spezielle Bewertungskriterien",
        content: "Bei Oldtimern gelten besondere Bewertungsmaßstäbe. Neben dem technischen Zustand spielen Originalität, Seltenheit und Dokumentation eine wichtige Rolle.",
        list: ["Zustandsnoten 1-5", "Originalitätsbewertung", "Dokumentation & Historie", "Seltenheitsfaktor", "Sammlerrelevanz"]
      },
      {
        title: "Gutachten für H-Kennzeichen",
        content: "Für die Beantragung des H-Kennzeichens benötigen Sie ein Oldtimer-Gutachten nach § 23 StVZO. Wir prüfen, ob Ihr Fahrzeug die Voraussetzungen erfüllt.",
        list: ["Mindestalter 30 Jahre", "Weitgehend originaler Zustand", "Guter Erhaltungszustand", "Erhaltenswertes Kulturgut"]
      }
    ],
    faq: [
      { q: "Ab wann gilt ein Fahrzeug als Oldtimer?", a: "Ein Fahrzeug gilt als Oldtimer, wenn es mindestens 30 Jahre alt ist und sich weitgehend im Originalzustand befindet." }
    ]
  }
};

const LeistungDetailPage = () => {
  const { slug } = useParams();
  const service = serviceData[slug];

  if (!service) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-4">Seite nicht gefunden</h1>
          <Link to="/leistungen" className="text-[#AA6622] font-bold">Zurück zu den Leistungen</Link>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <>
      <SEO 
        title={service.metaTitle}
        description={service.metaDescription}
        canonical={`/leistungen/${slug}`}
        keywords={`${service.title}, Kfz Gutachten, Hamburg, Schleswig-Holstein, Sachverständiger`}
      />
      {/* Breadcrumb */}
      <div className="bg-[#F7F7F7] py-4 border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#4A4A4A] hover:text-[#AA6622]">Startseite</Link>
            <span className="text-[#4A4A4A]">/</span>
            <Link to="/leistungen" className="text-[#4A4A4A] hover:text-[#AA6622]">Leistungen</Link>
            <span className="text-[#4A4A4A]">/</span>
            <span className="text-[#AA6622] font-medium">{service.title}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#F7F7F7] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/leistungen" className="inline-flex items-center text-[#AA6622] font-medium mb-6 hover:text-[#8e541b]">
            <ArrowLeft className="w-4 h-4 mr-2" />Alle Leistungen
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-[#AA6622] rounded-sm flex items-center justify-center mb-6">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">{service.title}</h1>
              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-8">{service.intro}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
                  <Phone className="w-5 h-5" /><span>Jetzt anrufen</span>
                </a>
                <Link to="/kontakt" className="border-2 border-black text-black px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors">
                  <MessageCircle className="w-5 h-5" /><span>Kontakt</span>
                </Link>
              </div>
            </div>
            {service.heroImage && (
              <div className="rounded-sm overflow-hidden">
                <img src={service.heroImage} alt={service.title} className="w-full h-80 object-cover" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {service.sections.map((section, index) => (
            <div key={index} className="mb-16 last:mb-0">
              <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">{section.title}</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-6">{section.content}</p>
              {section.list && (
                <ul className="space-y-3">
                  {section.list.map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#AA6622] flex-shrink-0 mt-0.5" />
                      <span className="text-[#4A4A4A]">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section className="py-20 bg-[#F7F7F7]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-black mb-8 font-['Outfit']">Häufige Fragen zu {service.title}</h2>
            <div className="space-y-4">
              {service.faq.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-sm">
                  <h3 className="font-bold text-black mb-2">{item.q}</h3>
                  <p className="text-[#4A4A4A]">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Jetzt {service.title} anfragen</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">Kontaktieren Sie uns für eine kostenlose Erstberatung. Wir sind täglich von 9:00 bis 23:00 Uhr für Sie erreichbar.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
              <Phone className="w-5 h-5" /><span>0174 893 6514</span>
            </a>
            <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#20bd5a] transition-colors">
              <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default LeistungDetailPage;
